export type DiffOp = { kind: 'equal' | 'insert' | 'delete'; text: string };

const MAX_CELLS = 4_000_000;

function lcsDiff(a: string[], b: string[]): DiffOp[] {
  const n = a.length;
  const m = b.length;
  // Trim common prefix and suffix first; most review diffs are small edits.
  let start = 0;
  while (start < n && start < m && a[start] === b[start]) start++;
  let endA = n;
  let endB = m;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  const ops: DiffOp[] = [];
  if (start > 0) ops.push({ kind: 'equal', text: a.slice(0, start).join('') });
  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);
  const rows = midA.length;
  const cols = midB.length;
  if (rows * cols > MAX_CELLS) {
    if (rows) ops.push({ kind: 'delete', text: midA.join('') });
    if (cols) ops.push({ kind: 'insert', text: midB.join('') });
  } else {
    const dp: Uint32Array[] = Array.from({ length: rows + 1 }, () => new Uint32Array(cols + 1));
    for (let i = rows - 1; i >= 0; i--) {
      const row = dp[i]!;
      const below = dp[i + 1]!;
      for (let j = cols - 1; j >= 0; j--) {
        row[j] = midA[i] === midB[j] ? below[j + 1]! + 1 : Math.max(below[j]!, row[j + 1]!);
      }
    }
    let i = 0;
    let j = 0;
    const push = (kind: DiffOp['kind'], text: string) => {
      const last = ops[ops.length - 1];
      if (last && last.kind === kind) last.text += text;
      else ops.push({ kind, text });
    };
    while (i < rows && j < cols) {
      if (midA[i] === midB[j]) {
        push('equal', midA[i]!);
        i++;
        j++;
      } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
        push('delete', midA[i]!);
        i++;
      } else {
        push('insert', midB[j]!);
        j++;
      }
    }
    while (i < rows) push('delete', midA[i++]!);
    while (j < cols) push('insert', midB[j++]!);
  }
  if (endA < n) {
    const tail = a.slice(endA).join('');
    const last = ops[ops.length - 1];
    if (last && last.kind === 'equal') last.text += tail;
    else ops.push({ kind: 'equal', text: tail });
  }
  return ops;
}

/** Word level diff that keeps whitespace tokens, so whitespace changes stay visible. */
export function diffWords(before: string, after: string): DiffOp[] {
  if (before === after) return before ? [{ kind: 'equal', text: before }] : [];
  const tokenize = (s: string) => s.split(/(\s+)/).filter((t) => t !== '');
  return lcsDiff(tokenize(before), tokenize(after));
}

export function diffLists(before: readonly string[], after: readonly string[]) {
  const b = new Set(before);
  const a = new Set(after);
  return {
    removed: before.filter((x) => !a.has(x)),
    added: after.filter((x) => !b.has(x)),
    kept: after.filter((x) => b.has(x)),
  };
}
